import React, { Fragment, useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import SEO from "../../components/SEO/SEO";
import Header from "../../components/Header/Header";
import PageTitle from "../../components/pagetitle/PageTitle";
import Footer from "../../components/footer/Footer";
import Scrollbar from "../../components/scrollbar/scrollbar";
import { Link } from "react-router-dom";
import { PRICING_FAQ } from "../../api/pricing";
import { STATUTORY, identifierLine, hasDpiit } from "../../config/statutoryIdentifiers";

// Images
import faqBg from "../../images/bg/faq-bg.png";
import subLeftIcon from "../../images/icon/sub-left-icon.png";
import subRightIcon from "../../images/icon/sub-right-icon.png";

// FAQ category definitions with item counts (must match en.json faqData structure)
const FAQ_CATEGORIES = [
  { key: "general", count: 8 },
  { key: "languages", count: 5 },
  { key: "features", count: 11 },
  { key: "industries", count: 8 },
  { key: "link", count: 6 },
  { key: "chorus", count: 9 },
  { key: "connect", count: 5 },
  { key: "storefront", count: 5 },
  { key: "import", count: 5 },
  { key: "mobile", count: 4 },
  { key: "technical", count: 6 },
];

const CATEGORY_LABELS: Record<string, string> = {
  all: "All Questions",
  general: "General",
  languages: "81 Languages",
  features: "Features",
  industries: "Industries",
  link: "LINK Phone",
  chorus: "CHORUS Social",
  connect: "CONNECT Chat",
  storefront: "Storefront",
  import: "Data Import",
  mobile: "Mobile App",
  pricing: "Pricing",
  technical: "Technical",
};

// Statutory identifiers appended verbatim to the answers that need them, and
// deliberately NOT routed through i18n. Machine translation does not merely
// reformat these — measured on 2026-08-11 across 80 locales, it transliterated
// the GSTIN into Cyrillic (sr) and Devanagari (doi), renumbered the PIN code
// into Bengali digits (as, bn, gu, mr, pa, ur, …) and, in zh-TW, replaced most
// of the registered address with invented text. A registration number that
// differs by language is worse than one shown only in English.
const FAQ_APPENDIX: Record<string, string> = {
  "general.q7":
    `${identifierLine(" · ")} · Incorporated ${STATUTORY.incorporatedOn} · ` +
    `Registered office: ${STATUTORY.registeredOffice}`,
  ...(hasDpiit()
    ? {
        "general.q8":
          `DPIIT Certificate of Recognition ${STATUTORY.dpiit} · issued ${STATUTORY.dpiitIssued} · ` +
          `valid to ${STATUTORY.dpiitValidTo} · Department for Promotion of Industry and Internal ` +
          `Trade, Ministry of Commerce and Industry, Government of India.`,
      }
    : {}),
};

interface FAQItem {
  id: number;
  question: string;
  answer: string;
  category: string;
}

const FAQPage: React.FC = () => {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const [activeId, setActiveId] = useState<number | null>(1);

  // Build FAQ list from i18n keys
  const allFAQs = useMemo(() => {
    const faqs: FAQItem[] = [];
    let counter = 0;

    // i18n-backed categories
    for (const cat of FAQ_CATEGORIES) {
      for (let i = 1; i <= cat.count; i++) {
        const q = t(`faqData.${cat.key}.q${i}.question`, { defaultValue: "" });
        const a = t(`faqData.${cat.key}.q${i}.answer`, { defaultValue: "" });
        if (q && a) {
          const appendix = FAQ_APPENDIX[`${cat.key}.q${i}`];
          faqs.push({
            id: ++counter,
            question: q,
            answer: appendix ? `${a} ${appendix}` : a,
            category: cat.key,
          });
        }
      }
    }

    // Pricing FAQ (from pricing.tsx, not i18n)
    for (const item of PRICING_FAQ) {
      faqs.push({ id: ++counter, question: item.question, answer: item.answer, category: "pricing" });
    }

    return faqs;
  }, [t]);

  const filteredFAQs =
    activeCategory === "all"
      ? allFAQs
      : allFAQs.filter((faq) => faq.category === activeCategory);

  // JSON-LD FAQPage — Google AI Overviews / ChatGPT / Perplexity extract Q&A pairs
  // from this. Built from the same allFAQs source the visual accordion uses.
  const faqSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: allFAQs.map((f) => ({
        "@type": "Question",
        name: f.question,
        acceptedAnswer: { "@type": "Answer", text: f.answer },
      })),
    }),
    [allFAQs],
  );

  const toggleFAQ = (id: number) => {
    setActiveId(activeId === id ? null : id);
  };

  return (
    <Fragment>
      <SEO
        title="FAQ"
        description="Frequently asked questions about AXOIX — pricing, features, voice commands, industry verticals, LINK phone system, and more."
        path="/faq"
        schema={faqSchema}
      />
      <div className="faq-page inner-page">
        <div className="body_wrap o-clip">
          <Header />
          <main>
            <PageTitle pageTitle={t('faq.pageTitle')} pagesub={t('faq.pageSub')} />

            {/* FAQ Section */}
            <section
              id="faq"
              className="faq pos-rel pt-145 pb-150 bg_img"
              style={{ backgroundImage: `url(${faqBg})` }}
            >
              <div className="container">
                {/* Section Title */}
                <div className="sec-title sec-title--two pricing-sec-title text-center mb-50">
                  <span className="sub-title mb-25 wow fadeInUp" data-wow-duration="600ms">
                    <img src={subLeftIcon} alt="icon" /> FAQ's{" "}
                    <img src={subRightIcon} alt="icon" />
                  </span>
                  <h2 className="title wow fadeInUp" data-wow-delay="150ms" data-wow-duration="600ms">
                    {t('faq.sectionTitle')}
                  </h2>
                </div>

                {/* Category Tabs */}
                <div className="text-center mb-50">
                  <div className="career-menu ul_li">
                    {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                      <button
                        key={key}
                        className={activeCategory === key ? "active" : ""}
                        onClick={() => {
                          setActiveCategory(key);
                          setActiveId(null);
                        }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* FAQ Accordion */}
                <div className="row">
                  <div className="col-lg-12">
                    <div className="xb-faq wow fadeInUp" data-wow-duration="600ms">
                      <ul className="accordion_box clearfix list-unstyled">
                        {filteredFAQs.map((faq, index) => (
                          <li
                            key={faq.id}
                            className={`accordion block ${activeId === faq.id ? "active-block" : ""}`}
                          >
                            {/* Question Button */}
                            <div
                              className={`acc-btn ${activeId === faq.id ? "active" : ""}`}
                              onClick={() => toggleFAQ(faq.id)}
                            >
                              <span className="number">
                                {index + 1 < 10 ? `0${index + 1}` : index + 1}
                              </span>
                              _ {faq.question}
                              <span className="arrow">
                                <span></span>
                              </span>
                            </div>

                            {/* Answer Body */}
                            <div
                              className={`acc_body ${activeId === faq.id ? "current" : ""}`}
                              style={{
                                maxHeight: activeId === faq.id ? "500px" : "0px",
                                overflow: "hidden",
                                transition: "max-height 0.4s ease",
                              }}
                            >
                              <div className="content">
                                <p>{faq.answer}</p>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* CTA Section */}
                <div className="text-center mt-50">
                  <Link to="/contact" className="thm-btn agency-btn">
                    <span className="text">{t('faq.ctaButton') || "Contact Support"}</span>
                    <span className="arrow">
                      <span className="arrow-icon">
                        <svg width="20" height="19" viewBox="0 0 20 19" fill="none"><rect x="3.783" y="13.463" width="14.31" height="1.817" transform="rotate(-40.2798 3.783 13.463)" fill="white"/><rect x="5.807" y="4.605" width="1.817" height="1.817" transform="rotate(-40.2798 5.807 4.605)" fill="white"/><rect x="8.367" y="4.816" width="1.817" height="1.817" transform="rotate(-40.2798 8.367 4.816)" fill="white"/><rect x="10.926" y="5.028" width="1.817" height="1.817" transform="rotate(-40.2798 10.926 5.028)" fill="white"/><rect x="13.277" y="7.8" width="1.817" height="1.817" transform="rotate(-40.2798 13.277 7.8)" fill="white"/><rect x="13.066" y="10.361" width="1.817" height="1.817" transform="rotate(-40.2798 13.066 10.361)" fill="white"/><rect x="12.855" y="12.922" width="1.817" height="1.817" transform="rotate(-40.2798 12.855 12.922)" fill="white"/></svg>
                        <svg width="20" height="19" viewBox="0 0 20 19" fill="none"><rect x="3.783" y="13.463" width="14.31" height="1.817" transform="rotate(-40.2798 3.783 13.463)" fill="white"/><rect x="5.807" y="4.605" width="1.817" height="1.817" transform="rotate(-40.2798 5.807 4.605)" fill="white"/><rect x="8.367" y="4.816" width="1.817" height="1.817" transform="rotate(-40.2798 8.367 4.816)" fill="white"/><rect x="10.926" y="5.028" width="1.817" height="1.817" transform="rotate(-40.2798 10.926 5.028)" fill="white"/><rect x="13.277" y="7.8" width="1.817" height="1.817" transform="rotate(-40.2798 13.277 7.8)" fill="white"/><rect x="13.066" y="10.361" width="1.817" height="1.817" transform="rotate(-40.2798 13.066 10.361)" fill="white"/><rect x="12.855" y="12.922" width="1.817" height="1.817" transform="rotate(-40.2798 12.855 12.922)" fill="white"/></svg>
                      </span>
                    </span>
                  </Link>
                </div>
              </div>

              {/* Decorative Shape */}
              <span className="faq-linear-shape"></span>
            </section>
          </main>
          <Footer />
          <Scrollbar />
        </div>
      </div>
    </Fragment>
  );
};

export default FAQPage;
